<template>
	<div
		class="tag-list-ct"
		:class="{ 'is-mobile': isMobileWebApp }"
	>
		<FilterableList
			:title="$I18n.trans('commons.all_types', { type: $I18n.trans_choice('commons.clip', 2) })"
			:subtitle="$I18n.trans('highlights.highlight_tool.tag_list.subtitle')"
			:active-filters="activeFilters"
			:filter-no-selection-label="filtersLabel"
			filters-module="tagFilters"
			modifier="highlight-tool"
			:hide-title-visibility="isSearchActive"
			header-padding-horizontal="xs"
			header-padding-vertical="xs"
			@filters-updated="updateAndApplyActiveFilters"
		>
			<template #over-title>
				<div
					v-if="isSearchActive"
					class="search"
				>
					<ClipSearchInput
						:placeholder="transUcfirst('one.clip_search.search_in_clips')"
						:search-keyword="searchKeyword"
						@close="closeOrClearSearch"
						@change="onSearchInput"
					/>
				</div>
			</template>
			<template #filters-dropdown="{ filtersModule }">
				<div class="actions">
					<SearchButton
						v-if="isSearchBtnVisible && !isSearchActive"
						@click="isSearchActive = true"
					/>
					<SortButton
						:sort-dir="sortDir"
						@sort-updated="handleSortUpdated"
					/>
					<ClipFilters
						:title="filtersListTitle"
						:module-name="filtersModule"
						:show-filters-count="isMobileWebApp"
						:search-keyword="searchKeyword"
						@filters-updated="updateAndApplyActiveFilters"
					/>
				</div>
			</template>
			<template #list-view>
				<TagGrid
					:enable-recycler-view="true"
					:recycler-page-size="25"
					:items="eventItems"
					:selected-items="selectedClips"
					:highlight-iq-selected-items="highlightIqSelectedClips"
					:hovered-item-id="hoveredClipId"
					:sport-name="sportName"
					:swipable="false"
					:is-highlight-tool="true"
					:checkbox-always-visible="checkboxAlwaysVisible"
					:currently-previewing-tag="currentlyPreviewingTag"
					@row-click="handleRowClick"
					@selection-changed="handleSelectionChanged"
					@preview="(tag) => $emit('preview', tag)"
					@stop-preview="() => $emit('stop-preview')"
					@row-hover="handleRowHover"
					@row-hover-end="handleRowHoverEnd"
				>
					<template #no-results>
						<NoResults
							v-if="searchKeyword"
							:title="emptyMsg.title"
							:description="emptyMsg.description"
						/>
					</template>
				</TagGrid>
			</template>
		</FilterableList>
	</div>
</template>

<script>
/* eslint-disable vue/one-component-per-file */
import { mapActions, mapGetters } from 'vuex';
import FilterableList from '@components/lists/FilterableList';
import TagGrid from '@videotools/js/one/components/TagList/TagGrid';
import ClipFilters from '@components/clip-list/ClipFilters';
import SortButton from '@components/clip-list/SortButton';
import ClipSearchInput from '@components/search/ClipSearchInput';
import SearchButton from '@components/search/SearchButton';
import NoResults from '@videotools/js/one/components/TagList/NoResults';
import { FILTER_GROUP_TAG } from '@js/store/modules/clip-filters';
import { TAG_NAMES, TAG_TYPE_CLOCK } from '@js/api/entities/TagButton';

const ITEMS_PER_LOAD = 15;

export default {
	name:       'HighlightTagList',
	components: {
		NoResults,
		SearchButton,
		ClipSearchInput,
		SortButton,
		ClipFilters,
		TagGrid,
		FilterableList,
	},
	props: {
		checkboxAlwaysVisible: {
			type:    Boolean,
			default: false,
		},
		currentlyPreviewingTag: {
			type:    Object,
			default: null,
		},
		hoveredClipId: {
			type:    Number,
			default: null,
		},
	},
	data() {
		return {
			filtersKeyPrefix:   'taglist',
			tags:               [],
			sortDir:            'desc',
			itemsLimit:         ITEMS_PER_LOAD,
			isLastPage:         false,
			isSearchActive:     false,
			isSearchBtnVisible: true,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:           'app/isMobileWebApp',
			isTouchDevice:            'device/isTouchDevice',
			event:                    'event/event',
			sportName:                'event/sportName',
			eventTags:                'event/tags',
			activeFilters:            'tagFilters/activeFilters',
			searchKeyword:            'tagFilters/searchKeyword',
			sorting:                  'tags/sorting',
			canEditEventTag:          'permissions/canEditEventTag',
			selectedClips:            'highlightTool/selectedClips',
			highlightIqSelectedClips: 'highlightTool/highlightIqSelectedClips',
		}),
		eventItems() {
			const evTags = this.$store.getters['tagFilters/filteredClips'];
			const items = this.sortTags(evTags, this.sortDir).map((tag) => ({
				id:              tag.id,
				tag,
				name:            tag.name,
				time:            tag.formattedTimestamp,
				duration:        tag.formattedDuration,
				durationSeconds: tag.duration,
				imageUrl:        tag.imageUrl,
				player:          tag.player,
				team:            tag.teamName,
				timestamp:       tag.timestamp,
				participantName: tag.participantName,
				action:          tag.name,
				gameMinute:      tag.gameTime?.gameMinute,
				tooltip:         tag.isOutsideVideo ? this.$I18n.trans('one.tag_outside_video') : null,
				isOutsideVideo:  tag.isOutsideVideo,
				rating:          tag.rating,
				audioScore:      tag.iqScore?.audioScore ?? 0,
			}));

			// exclude clock tags if no clock filters are set
			if (!this.activeFilters.some(filter =>
				filter.type === FILTER_GROUP_TAG && TAG_NAMES[TAG_TYPE_CLOCK].includes(filter.name)
			)) {
				return items.filter(item => !TAG_NAMES[TAG_TYPE_CLOCK].includes(item.tag.name));
			}

			return items;
		},
		filtersLabel() {
			if (!this.$I18n) return 'All Tags';
			return this.$I18n.trans('commons.all_types', {
				type: this.transChoiceUcFirst('one.tag', 2),
			});
		},
		filtersListTitle() {
			return `${this.transUcfirst('commons.filter')} ${this.transChoiceUcFirst('commons.tag', 2)}`;
		},
		emptyMsg() {
			return {
				title:       this.transUcfirst('one.clip_search.no_results_for', { keyword: this.searchKeyword }),
				description: this.transUcfirst('one.clip_search.no_results_text'),
			};
		},
	},
	watch: {
		eventTags() {
			this.initEventTags();
		},
	},
	created() {
		if (this.sorting.TagList) this.sortDir = this.sorting.TagList.direction;
		this.initEventTags();

		this.isSearchActive = this.searchKeyword?.length > 0;
		this.isSearchBtnVisible = !this.isSearchActive;
	},
	methods: {
		...mapActions({
			deleteEventTags:       'event/deleteEventTags',
			createVideo:           'tags/createVideo',
			updateActiveFilters:   'tagFilters/updateActiveFilters',
			searchClips:           'tagFilters/searchClips',
			clearSearch:           'tagFilters/clearSearch',
			setSorting:            'tags/setSorting',
			setTimelineActiveTags: 'timelineTags/setActiveTags',
		}),
		initEventTags() {
			this.updateTimelineTags();
		},
		async updateAndApplyActiveFilters(filters) {
			await this.updateActiveFilters(filters);
			this.updateTimelineTags();
		},
		handleSelectionChanged(selectedItems) {
			this.$emit('selection-changed', selectedItems);
		},
		handleEdit(item) {
			this.$emit('edit', [item]);
		},
		handlePlay(item) {
			this.$emit('play', item);
		},
		handleRowClick(item) {
			this.handleEdit(item);
		},
		handleRowHover(item) {
			this.$emit('clip-hover', item);
		},
		handleRowHoverEnd() {
			this.$emit('clip-hover-end');
		},
		clearSelection() {
			this.$emit('selection-changed', []);
		},
		handleSortUpdated(attribute, dir) {
			this.setSorting({
				component: 'TagList',
				key:       attribute,
				direction: dir,
			});
			this.sortDir = dir;
		},
		sortTags(tags, dir) {
			const ret = dir === 'asc' ? -1 : 1;
			tags.sort((tagA, tagB) => {
				return tagA.timestamp > tagB.timestamp ? ret : -ret;
			});
			return tags;
		},
		updateTimelineTags() {
			this.setTimelineActiveTags(this.$store.getters['tagFilters/filteredClips']);
		},
		closeOrClearSearch() {
			if (this.searchKeyword === null) {
				this.isSearchActive = false;
			}
			this.clearSearch();
		},
		onSearchInput(value) {
			this.searchClips(value);
			this.updateTimelineTags();
		},
	},
};
</script>

<style lang="scss" scoped>
@import "@shared/sass/shared-variables";
@import "@videotools/sass/_fonts.scss";

.tag-list-ct {
	display: flex;
	flex-direction: column;
	height: 100%;
}

.footer {
	width: 100%;
}

.actions {
	display: flex;
	gap: $sp5;
}

.search {
	display: flex;
	align-items: center;
	position: absolute;
	width: 100%;
	height: 100%;
}

.title-wrapper {
	position: absolute;

	.is-mobile & {
		h2 {
			@include text-md-2;
		}
	}
}
</style>
